// §2.2 / §4.1 推導層。
// 所有畫面看到的東西一律由 events 推導，不另存一份。本檔的函式全部是純函式。
import { round } from '../lib/format.js';
// ---------------------------------------------------------------- 基準計算
export function specOf(config, modelCode) {
    if (!modelCode)
        return null;
    return config.modelSpecs.find((s) => s.modelCode === modelCode) ?? null;
}
/**
 * 這筆基準完整到可以拿來算數字嗎。
 *
 * 暫定型號（現場先建起來、基準還沒補）**一律不參與計算**。它的用途是保留
 * 「這批是哪個型號」這個資訊，數字則走未辨識那條路，直到主管補齊基準並覆核。
 *
 * 為什麼一定要擋：暫定型號的 `totalKg` 可能是 0、`composition` 可能是空的。
 * 放它進來會有兩個都很難查的後果——
 *   1. 它自己的 `materialRatio` 全是 0，掛這個型號的板子分攤基準變成 0，
 *      同一料件裡其他板子的佔比就被悄悄放大了；
 *   2. 它會被算進未辨識的平均基準，把**全公司**每一片未辨識板子的基準一起拉歪。
 * 兩者帳面上都「算得出數字」，只是全錯。
 */
export function specIsUsable(spec) {
    if (!spec)
        return false;
    if (spec.status === 'provisional')
        return false;
    if (!(typeof spec.totalKg === 'number' && spec.totalKg > 0))
        return false;
    const comp = spec.composition;
    if (!comp)
        return false;
    return Object.values(comp).some((v) => typeof v === 'number' && v > 0);
}
/** 有完整基準、可以拿來算數字的型號。暫定型號不在內。 */
export function usableSpecs(config) {
    return config.modelSpecs.filter(specIsUsable);
}
/** 找出可以拿來算數字的型號。找不到（或只找到暫定型號）就回 null，由呼叫端退回平均基準。 */
export function usableSpecOf(config, modelCode) {
    const spec = specOf(config, modelCode);
    return specIsUsable(spec) ? spec : null;
}
/** 未辨識型號時的替代基準：所有**有完整基準**的型號的平均 */
function fallbackSpec(config) {
    const specs = usableSpecs(config);
    const n = Math.max(1, specs.length);
    const avg = specs.reduce((a, s) => a + s.totalKg, 0) / n;
    const comp = {};
    for (const s of specs) {
        for (const [k, v] of Object.entries(s.composition)) {
            comp[k] = (comp[k] ?? 0) + (v ?? 0) / n;
        }
    }
    return { modelCode: '__FALLBACK__', label: '未辨識（取全型號平均）', cellCount: 0, totalKg: avg, composition: comp };
}
/** 單片對某物料的理論佔比（相對整片總重） */
export function materialRatio(spec, code) {
    const c = spec.composition;
    switch (code) {
        case 'PANEL':
            return 1;
        case 'PANEL_NJ':
            return 1 - (c.JBOX ?? 0);
        case 'GLAZED_LAM':
            return (c.GLASS ?? 0) + (c.LAMINATE ?? 0);
        default:
            return c[code] ?? 0;
    }
}
/**
 * 單片對某物料的分攤基準（kg）。
 * ★ v2.3 basisSource：若該片已有實測進料重，基準改用實測值，否則用型號理論值。
 */
export function panelBasis(world, config, panelId, code) {
    const panel = world.panels.get(panelId);
    // 暫定型號走 fallback：保留身分，但數字跟未辨識一樣（見 specIsUsable）
    const spec = usableSpecOf(config, panel?.modelCode ?? null) ?? fallbackSpec(config);
    const ratio = materialRatio(spec, code);
    const measured = world.panelIntakeKg.get(panelId);
    if (measured)
        return { basisKg: round(measured.kg * ratio, 4), basisSource: 'measured' };
    return { basisKg: round(spec.totalKg * ratio, 4), basisSource: 'spec_theoretical' };
}
export function homogeneityOf(world, panelIds) {
    const models = new Set();
    let unknown = 0;
    for (const p of panelIds) {
        const panel = world.panels.get(p);
        if (!panel || !panel.modelCode)
            unknown++;
        else
            models.add(panel.modelCode);
    }
    const kinds = models.size + (unknown > 0 ? 1 : 0);
    if (kinds <= 1)
        return 'A';
    if (kinds === 2)
        return 'B';
    return 'C';
}
// ---------------------------------------------------------------- 主 fold
let _cacheKey = null;
let _cacheVal = null;
export function deriveWorld(events, config) {
    if (_cacheKey && _cacheKey.events === events && _cacheKey.config === config && _cacheVal)
        return _cacheVal;
    const w = foldEvents(events, config);
    _cacheKey = { events, config };
    _cacheVal = w;
    return w;
}
function emptyWorld() {
    return {
        panels: new Map(), panelIntakeKg: new Map(), items: new Map(), attributions: [],
        genealogy: [], wipLots: new Map(), workOrders: new Map(), issueOrders: new Map(),
        issueLines: [], receiptOrders: new Map(), receiptLines: [], weighEvents: [],
        ledger: [], shipments: new Map(), deliveries: new Map(), idRecords: new Map(),
        reservations: new Map(), configChangeCount: 0, scrappedPanels: new Set(),
    };
}
let ledgerSeq = 0;
function nextLedgerId() {
    ledgerSeq += 1;
    return `LG-${String(ledgerSeq).padStart(7, '0')}`;
}
function foldEvents(events, config) {
    const w = emptyWorld();
    ledgerSeq = 0;
    for (const ev of events) {
        switch (ev.type) {
            case 'IdBatchGenerated': {
                for (const id of ev.ids) {
                    w.idRecords.set(id, { id, idType: ev.idType, status: 'available', reservationId: null });
                }
                break;
            }
            case 'IdBatchReserved': {
                w.reservations.set(ev.reservationId, {
                    reservationId: ev.reservationId, idType: ev.idType, ids: ev.ids,
                    reservedBy: ev.by, deviceId: ev.deviceId, reservedAt: ev.at,
                    status: 'active', reclaimedBy: null, reclaimReason: null,
                });
                for (const id of ev.ids) {
                    const r = w.idRecords.get(id);
                    if (r)
                        w.idRecords.set(id, { ...r, status: 'reserved', reservationId: ev.reservationId });
                }
                break;
            }
            case 'IdReclaimRequested': {
                const r = w.reservations.get(ev.reservationId);
                if (r)
                    w.reservations.set(ev.reservationId, { ...r, status: 'reclaim_pending', reclaimReason: ev.reason, reclaimedBy: ev.by });
                break;
            }
            case 'IdReclaimCompleted': {
                const r = w.reservations.get(ev.reservationId);
                if (r)
                    w.reservations.set(ev.reservationId, { ...r, status: 'reclaimed' });
                for (const id of ev.reclaimedIds) {
                    const rec = w.idRecords.get(id);
                    if (rec && rec.status === 'reserved')
                        w.idRecords.set(id, { ...rec, status: 'available', reservationId: null });
                }
                break;
            }
            case 'IdVoided': {
                const rec = w.idRecords.get(ev.id);
                if (rec)
                    w.idRecords.set(ev.id, { ...rec, status: 'voided' });
                break;
            }
            case 'DeliveryReceived': {
                w.deliveries.set(ev.deliveryId, {
                    deliveryId: ev.deliveryId, declaredCount: ev.declaredCount, customer: ev.customer,
                    note: ev.note, at: ev.at, reservedCount: 0, registeredCount: 0,
                });
                break;
            }
            case 'PanelRegistered': {
                w.panels.set(ev.panel.panelId, { ...ev.panel });
                const rec = w.idRecords.get(ev.panel.panelId);
                if (rec)
                    w.idRecords.set(ev.panel.panelId, { ...rec, status: 'used' });
                // 更新該筆交貨的建檔數（進庫精靈把 deliveryId 放在 attributes 裡）
                const did = ev.panel.attributes?.deliveryId;
                const d = did ? w.deliveries.get(did) : undefined;
                if (d)
                    d.registeredCount += 1;
                break;
            }
            case 'ReceiptPosted': {
                applyReceipt(w, config, ev.receiptOrder, ev.lines, ev.weighEvents);
                break;
            }
            case 'WorkOrderOpened': {
                w.workOrders.set(ev.workOrder.workOrderId, { ...ev.workOrder });
                break;
            }
            case 'WorkOrderClosed': {
                const wo = w.workOrders.get(ev.workOrderId);
                if (wo)
                    w.workOrders.set(ev.workOrderId, { ...wo, status: 'closed', closedAt: ev.at });
                break;
            }
            case 'IssuePosted': {
                applyIssue(w, config, ev.issueOrder, ev.lines, ev.weighEvents, null);
                break;
            }
            case 'ShipmentPosted': {
                w.shipments.set(ev.shipment.shipmentId, { ...ev.shipment });
                applyIssue(w, config, ev.issueOrder, ev.lines, ev.weighEvents, ev.shipment.shipmentId);
                break;
            }
            case 'PanelScrapped': {
                const p = w.panels.get(ev.panelId);
                if (p)
                    w.panels.set(ev.panelId, { ...p, status: 'scrapped' });
                w.scrappedPanels.add(ev.panelId);
                break;
            }
            case 'PanelDetached': {
                const p = w.panels.get(ev.panelId);
                if (p)
                    w.panels.set(ev.panelId, { ...p, status: 'detached' });
                const it = w.items.get(ev.fromItemId);
                if (it) {
                    const rest = it.panelIds.filter((x) => x !== ev.panelId);
                    w.items.set(ev.fromItemId, { ...it, panelIds: rest, panelCount: rest.length });
                }
                break;
            }
            case 'StockCounted': {
                // 盤點只留軌跡，不改帳（差異以 AdjustmentPosted 處理）
                break;
            }
            case 'AdjustmentPosted': {
                const target = w.ledger.find((l) => l.ledgerId === ev.targetLedgerId);
                if (target) {
                    w.ledger.push({
                        ...target, ledgerId: nextLedgerId(), entryType: 'reversal',
                        qtyKg: ev.qtyKg, reversesLedgerId: target.ledgerId, postedAt: ev.at,
                        note: ev.reason,
                    });
                }
                break;
            }
            case 'ConfigChanged': {
                w.configChangeCount += 1;
                break;
            }
            case 'ScenarioLoaded':
                break;
        }
    }
    // 工單損耗分攤（需在所有事件處理完後才知道站台是否已結算）
    allocateStationLoss(w, config);
    return w;
}
// ---------------------------------------------------------------- 入庫／繳庫
function applyReceipt(w, config, order, lines, weighs) {
    w.receiptOrders.set(order.receiptOrderId, { ...order });
    for (const we of weighs)
        w.weighEvents.push({ ...we });
    const wo = order.workOrderId ? w.workOrders.get(order.workOrderId) : null;
    // ---- 補秤（§10 步 17）----
    // weighEvents 指向「已存在的繳庫行」時，只補上重量，不建立新料件。
    // 事件仍然只新增（§13.3）：原本那筆無重量的繳庫行不會被刪，是被後來的量測補完。
    const supplementIds = new Set();
    for (const we of weighs) {
        if (!we.receiptLineId)
            continue;
        if (lines.some((l) => l.receiptLineId === we.receiptLineId))
            continue;
        if (w.receiptLines.some((x) => x.receiptLineId === we.receiptLineId))
            supplementIds.add(we.receiptLineId);
    }
    for (const rlId of supplementIds) {
        applySupplementaryWeigh(w, config, rlId, weighs, order.createdAt);
    }
    for (const line of lines) {
        w.receiptLines.push({ ...line });
        const idRec = w.idRecords.get(line.newItemId);
        if (idRec)
            w.idRecords.set(line.newItemId, { ...idRec, status: 'used' });
        const perPanelWeighs = weighs.filter((x) => x.receiptLineId === line.receiptLineId && x.panelId !== null);
        const isPerPanel = perPanelWeighs.length > 0;
        // 若是逐片繳庫，netKg 由逐片合計
        const netKg = isPerPanel
            ? round(perPanelWeighs.reduce((a, x) => a + x.netKg, 0), 3)
            : line.netKg;
        // 進料重量登錄（RCV 過磅，或逐片量測時建立分母）
        if (isPerPanel) {
            for (const pw of perPanelWeighs) {
                if (line.materialCode === 'PANEL' && pw.panelId && !w.panelIntakeKg.has(pw.panelId)) {
                    w.panelIntakeKg.set(pw.panelId, { kg: pw.netKg, source: 'receiving' });
                }
            }
        }
        else if (line.materialCode === 'PANEL' && netKg !== null && line.panelIds.length > 0) {
            // 整批進庫過磅：按理論基準分攤成每片的進料重
            const bases = line.panelIds.map((p) => panelBasis(w, config, p, 'PANEL').basisKg);
            const total = bases.reduce((a, b) => a + b, 0) || 1;
            line.panelIds.forEach((p, i) => {
                if (!w.panelIntakeKg.has(p)) {
                    w.panelIntakeKg.set(p, { kg: round((netKg * bases[i]) / total, 4), source: 'receiving' });
                }
            });
        }
        // 分攤：計算每片的 shareRatio
        const attrs = [];
        let calcMethod = 'weighted_average';
        let resolution = 'R2';
        if (line.panelIds.length === 0) {
            resolution = netKg && netKg > 0 ? 'R4' : 'R2';
        }
        else if (isPerPanel && perPanelWeighs.length === line.panelIds.length) {
            // 逐片實測 → R1、直接歸屬
            calcMethod = 'direct';
            resolution = 'R1';
            const total = perPanelWeighs.reduce((a, x) => a + x.netKg, 0) || 1;
            for (const pw of perPanelWeighs) {
                attrs.push({
                    itemId: line.newItemId, panelId: pw.panelId,
                    shareRatio: pw.netKg / total, basisKg: pw.netKg, basisSource: 'measured',
                    calcMethod: 'direct',
                });
            }
        }
        else {
            const bases = line.panelIds.map((p) => panelBasis(w, config, p, line.materialCode));
            const total = bases.reduce((a, b) => a + b.basisKg, 0) || 1;
            const anyMeasured = bases.some((b) => b.basisSource === 'measured');
            const allMeasured = bases.every((b) => b.basisSource === 'measured');
            calcMethod = allMeasured || !anyMeasured ? 'weighted_average' : 'mixed_average';
            line.panelIds.forEach((p, i) => {
                attrs.push({
                    itemId: line.newItemId, panelId: p, shareRatio: bases[i].basisKg / total,
                    basisKg: bases[i].basisKg, basisSource: bases[i].basisSource, calcMethod,
                });
            });
        }
        // 來源批的 R3 汙染會傳給產出
        let taint = false;
        if (order.workOrderId) {
            for (const lot of w.wipLots.values()) {
                if (lot.workOrderId === order.workOrderId && lot.splitBasis === 'by_weight')
                    taint = true;
            }
        }
        if (taint && resolution === 'R2')
            resolution = 'R3';
        const homogeneity = homogeneityOf(w, line.panelIds);
        const item = {
            itemId: line.newItemId,
            materialCode: line.materialCode,
            containerId: line.containerId,
            locationId: line.locationId,
            netKg: netKg,
            panelCount: line.panelIds.length || null,
            resolutionGrade: resolution,
            homogeneityGrade: homogeneity,
            status: 'in_stock',
            sourceReceiptLineId: line.receiptLineId,
            createdAt: order.createdAt,
            closedAt: null,
            panelIds: [...line.panelIds],
            workOrderId: order.workOrderId,
            r3Taint: taint,
            issuedPanelIds: [],
        };
        w.items.set(item.itemId, item);
        w.attributions.push(...attrs);
        // 家系：本工單消耗的批 → 本產出
        if (order.workOrderId) {
            for (const lot of w.wipLots.values()) {
                if (lot.workOrderId === order.workOrderId) {
                    w.genealogy.push({ parentItemId: lot.sourceItemId, childItemId: item.itemId, workOrderId: order.workOrderId });
                }
            }
        }
        // 單片收支帳：產出
        if (netKg !== null) {
            for (const a of attrs) {
                w.ledger.push({
                    ledgerId: nextLedgerId(), panelId: a.panelId, materialCode: line.materialCode,
                    entryType: 'output', qtyKg: round(netKg * a.shareRatio, 4),
                    stationId: wo?.stationId ?? 'RCV', resourceId: wo?.resourceId ?? 'RCV-1',
                    workOrderId: order.workOrderId, receiptLineId: line.receiptLineId, issueLineId: null,
                    shipmentId: null, calcMethod: a.calcMethod, resolutionGrade: resolution,
                    homogeneityGrade: homogeneity, dataTier: 'primary', reversesLedgerId: null,
                    postedAt: order.createdAt,
                });
            }
        }
    }
}
/** 補秤：為既有的繳庫行補上重量，並補記單片收支帳的產出分錄。 */
function applySupplementaryWeigh(w, config, receiptLineId, weighs, atIso) {
    const rl = w.receiptLines.find((x) => x.receiptLineId === receiptLineId);
    if (!rl)
        return;
    const item = w.items.get(rl.newItemId);
    if (!item)
        return;
    const mine = weighs.filter((x) => x.receiptLineId === receiptLineId);
    const perPanel = mine.filter((x) => x.panelId !== null);
    const netKg = perPanel.length > 0
        ? round(perPanel.reduce((a, x) => a + x.netKg, 0), 3)
        : mine.find((x) => x.panelId === null)?.netKg ?? null;
    if (netKg === null)
        return;
    rl.netKg = netKg;
    const ro = w.receiptOrders.get(rl.receiptOrderId);
    const wo = ro?.workOrderId ? w.workOrders.get(ro.workOrderId) : null;
    // 若補的是逐片量測，分攤升級為 R1、直接歸屬
    let resolution = item.resolutionGrade;
    let attrs = w.attributions.filter((a) => a.itemId === item.itemId);
    if (perPanel.length > 0 && perPanel.length === rl.panelIds.length) {
        resolution = 'R1';
        w.attributions = w.attributions.filter((a) => a.itemId !== item.itemId);
        const total = perPanel.reduce((a, x) => a + x.netKg, 0) || 1;
        attrs = perPanel.map((pw) => ({
            itemId: item.itemId, panelId: pw.panelId,
            shareRatio: pw.netKg / total, basisKg: pw.netKg,
            basisSource: 'measured', calcMethod: 'direct',
        }));
        w.attributions.push(...attrs);
    }
    w.items.set(item.itemId, { ...item, netKg, resolutionGrade: resolution });
    for (const a of attrs) {
        w.ledger.push({
            ledgerId: nextLedgerId(), panelId: a.panelId, materialCode: rl.materialCode,
            entryType: 'output', qtyKg: round(netKg * a.shareRatio, 4),
            stationId: wo?.stationId ?? null, resourceId: wo?.resourceId ?? null,
            workOrderId: ro?.workOrderId ?? null, receiptLineId: rl.receiptLineId, issueLineId: null,
            shipmentId: null, calcMethod: a.calcMethod, resolutionGrade: resolution,
            homogeneityGrade: item.homogeneityGrade, dataTier: 'primary', reversesLedgerId: null,
            postedAt: atIso, note: '補秤：啟用量測點後補上本筆重量',
        });
    }
}
// ---------------------------------------------------------------- 領料／出廠
function applyIssue(w, config, order, lines, weighs, shipmentId) {
    w.issueOrders.set(order.issueOrderId, { ...order });
    for (const we of weighs)
        w.weighEvents.push({ ...we });
    const wo = order.workOrderId ? w.workOrders.get(order.workOrderId) : null;
    for (const line of lines) {
        w.issueLines.push({ ...line });
        const src = w.items.get(line.itemId);
        if (!src)
            continue;
        const takenPanels = line.isPartial && line.splitBasis === 'by_id' && line.pickedPanelIds
            ? line.pickedPanelIds
            : [...src.panelIds];
        const perPanelWeighs = weighs.filter((x) => x.issueLineId === line.issueLineId && x.panelId !== null);
        const isPerPanel = perPanelWeighs.length > 0;
        // ★ 逐片實測：建立／更新該片的進料重量分母（§9 進庫不過磅的補救）
        if (isPerPanel) {
            for (const pw of perPanelWeighs) {
                if (pw.panelId && !w.panelIntakeKg.has(pw.panelId)) {
                    w.panelIntakeKg.set(pw.panelId, { kg: pw.netKg, source: 'op1_measured' });
                }
            }
        }
        const issuedKg = isPerPanel
            ? round(perPanelWeighs.reduce((a, x) => a + x.netKg, 0), 3)
            : line.issuedKg;
        // 分攤到片
        let resolution;
        let calcMethod;
        const shares = new Map();
        if (isPerPanel && perPanelWeighs.length === takenPanels.length) {
            resolution = 'R1';
            calcMethod = 'direct';
            const total = perPanelWeighs.reduce((a, x) => a + x.netKg, 0) || 1;
            for (const pw of perPanelWeighs) {
                shares.set(pw.panelId, { ratio: pw.netKg / total, basisKg: pw.netKg, src: 'measured' });
            }
        }
        else {
            const bases = takenPanels.map((p) => panelBasis(w, config, p, src.materialCode));
            const total = bases.reduce((a, b) => a + b.basisKg, 0) || 1;
            const anyMeasured = bases.some((b) => b.basisSource === 'measured');
            const allMeasured = bases.every((b) => b.basisSource === 'measured');
            calcMethod = allMeasured || !anyMeasured ? 'weighted_average' : 'mixed_average';
            takenPanels.forEach((p, i) => {
                shares.set(p, { ratio: bases[i].basisKg / total, basisKg: bases[i].basisKg, src: bases[i].basisSource });
            });
            // §2.7 判定：by_weight 分裂 → 分攤跨集合，降級 R3
            if (line.isPartial && line.splitBasis === 'by_weight')
                resolution = 'R3';
            else if (src.r3Taint)
                resolution = 'R3';
            else
                resolution = src.resolutionGrade === 'R1' ? 'R1' : 'R2';
            if (resolution === 'R1')
                calcMethod = 'direct';
        }
        const homogeneity = homogeneityOf(w, takenPanels);
        const lot = {
            issueLineId: line.issueLineId, issueOrderId: order.issueOrderId,
            workOrderId: order.workOrderId, shipmentId,
            sourceItemId: line.itemId, materialCode: src.materialCode,
            panelIds: takenPanels, issuedKg, weightSource: line.weightSource,
            splitBasis: line.splitBasis, resolutionGrade: resolution,
            homogeneityGrade: homogeneity, consumed: false,
        };
        w.wipLots.set(line.issueLineId, lot);
        // 更新來源料件。
        // ★ 切走的比例要相對於「整個料件」，不是相對於挑走的那一份——
        //   shares 是在 takenPanels 內部正規化的（合計恆為 1），拿它當切分比例會把剩餘料件歸零。
        const remaining = src.panelIds.filter((p) => !takenPanels.includes(p));
        const allBasis = src.panelIds
            .reduce((a, p) => a + panelBasis(w, config, p, src.materialCode).basisKg, 0) || 1;
        const takenBasis = takenPanels
            .reduce((a, p) => a + panelBasis(w, config, p, src.materialCode).basisKg, 0);
        const takenRatio = takenBasis / allBasis;
        if (remaining.length === 0) {
            w.items.set(src.itemId, {
                ...src, status: order.issueType === 'production' ? 'issued' : order.issueType === 'shipment' ? 'shipped' : 'disposed',
                closedAt: order.createdAt, netKg: src.netKg,
                issuedPanelIds: [...src.panelIds],
            });
        }
        else {
            const restKg = src.netKg === null ? null
                : line.splitBasis === 'by_weight' && line.issuedKg !== null
                    ? round(src.netKg - line.issuedKg, 3)
                    : round(src.netKg * (1 - takenRatio), 3);
            w.items.set(src.itemId, {
                ...src,
                panelIds: line.splitBasis === 'by_weight' ? src.panelIds : remaining,
                panelCount: line.splitBasis === 'by_weight' ? src.panelCount : remaining.length,
                netKg: restKg,
                r3Taint: src.r3Taint || line.splitBasis === 'by_weight',
                resolutionGrade: line.splitBasis === 'by_weight' ? 'R3' : src.resolutionGrade,
                issuedPanelIds: [...src.issuedPanelIds, ...takenPanels],
            });
        }
        // ★ 逐片實測且進庫未過磅時，先補一筆 output 建立分母
        if (isPerPanel) {
            for (const pw of perPanelWeighs) {
                const pid = pw.panelId;
                const hasOutput = w.ledger.some((l) => l.panelId === pid && l.materialCode === src.materialCode && l.entryType === 'output');
                if (!hasOutput) {
                    w.ledger.push({
                        ledgerId: nextLedgerId(), panelId: pid, materialCode: src.materialCode,
                        entryType: 'output', qtyKg: pw.netKg,
                        stationId: wo?.stationId ?? null, resourceId: wo?.resourceId ?? null,
                        workOrderId: order.workOrderId, receiptLineId: null, issueLineId: line.issueLineId,
                        shipmentId: null, calcMethod: 'direct', resolutionGrade: 'R1',
                        homogeneityGrade: homogeneity, dataTier: 'primary', reversesLedgerId: null,
                        postedAt: order.createdAt,
                        note: '進庫未過磅，本片重量以 OP-1 逐片實測建立分母',
                    });
                }
            }
        }
        // 單片收支帳：消耗／出廠
        if (issuedKg !== null) {
            const entryType = order.issueType === 'production' ? 'consumed'
                : order.issueType === 'shipment' ? 'shipped'
                    : order.issueType === 'disposal' ? 'disposal' : 'consumed';
            for (const [pid, s] of shares) {
                w.ledger.push({
                    ledgerId: nextLedgerId(), panelId: pid, materialCode: src.materialCode,
                    entryType, qtyKg: -round(issuedKg * s.ratio, 4),
                    stationId: wo?.stationId ?? null, resourceId: wo?.resourceId ?? null,
                    workOrderId: order.workOrderId, receiptLineId: null, issueLineId: line.issueLineId,
                    shipmentId, calcMethod, resolutionGrade: resolution,
                    homogeneityGrade: homogeneity, dataTier: line.weightSource === 'measured' ? 'primary' : 'secondary',
                    reversesLedgerId: null, postedAt: order.createdAt,
                });
            }
        }
    }
}
// ---------------------------------------------------------------- 損耗分攤
/**
 * 站台損耗按領出基準分攤到片。無法結算的站台不分攤（帳上留白，由結算畫面說明）。
 *
 * 記帳方式：領出的料在本站分成「轉化為產出」與「損耗」兩部分，因此把既有的 consumed
 * 分錄拆成 consumed（減去損耗份額）＋ loss（損耗份額）。兩者相加仍等於領出重，
 * 單片的該物料餘額才會歸零——若另外再加一筆 loss 而不調整 consumed，餘額會變成負數。
 */
function allocateStationLoss(w, config) {
    for (const wo of w.workOrders.values()) {
        const lots = [...w.wipLots.values()].filter((l) => l.workOrderId === wo.workOrderId);
        if (lots.length === 0)
            continue;
        const rLines = w.receiptLines.filter((rl) => {
            const ro = w.receiptOrders.get(rl.receiptOrderId);
            return ro?.workOrderId === wo.workOrderId;
        });
        if (rLines.length === 0)
            continue;
        const issuedKg = lots.reduce((a, l) => a + (l.issuedKg ?? NaN), 0);
        const receivedKg = rLines.reduce((a, rl) => {
            const perPanel = w.weighEvents.filter((x) => x.receiptLineId === rl.receiptLineId && x.panelId !== null);
            const kg = perPanel.length > 0 ? perPanel.reduce((s, x) => s + x.netKg, 0) : rl.netKg;
            return a + (kg === null ? NaN : kg);
        }, 0);
        if (Number.isNaN(issuedKg) || Number.isNaN(receivedKg))
            continue; // 無法結算 → 不分攤
        const loss = round(issuedKg - receivedKg, 4);
        if (Math.abs(loss) < 1e-6)
            continue;
        // 依領出基準分攤
        const allPanels = [];
        for (const l of lots) {
            for (const p of l.panelIds) {
                allPanels.push({ pid: p, basis: panelBasis(w, config, p, l.materialCode).basisKg, code: l.materialCode });
            }
        }
        const total = allPanels.reduce((a, x) => a + x.basis, 0) || 1;
        for (const x of allPanels) {
            const q = -round((loss * x.basis) / total, 5);
            if (Math.abs(q) < 1e-7)
                continue;
            // 從該片本站的 consumed 分錄中扣掉損耗份額，避免重複計入
            const consumed = w.ledger.find((l) => l.workOrderId === wo.workOrderId && l.panelId === x.pid
                && l.materialCode === x.code && l.entryType === 'consumed');
            if (consumed)
                consumed.qtyKg = round(consumed.qtyKg - q, 5);
            w.ledger.push({
                ledgerId: nextLedgerId(), panelId: x.pid, materialCode: x.code,
                entryType: 'loss', qtyKg: q, stationId: wo.stationId, resourceId: wo.resourceId,
                workOrderId: wo.workOrderId, receiptLineId: null, issueLineId: null, shipmentId: null,
                calcMethod: 'weighted_average', resolutionGrade: 'R2', homogeneityGrade: 'A',
                dataTier: 'secondary', reversesLedgerId: null, postedAt: wo.closedAt ?? wo.openedAt,
                note: loss >= 0 ? '站台損耗分攤'
                    : wo.purged ? '負損耗：繳回大於領出，需查核量測'
                        : '負損耗（未清機殘留為預期行為）',
            });
        }
    }
}
//# sourceMappingURL=world.js.map